import _ from 'lodash';
import uuid from 'uuid/v4';
import { MAX_WEIGHT } from '../../appConstants';
import { search } from '../../utils';

import SoundsLikePhraseObject from './SoundsLikePhraseObject';

const TOTAL_ROWS = 30;
const ROWS_AT_END = 10;

class VocabularyListTermObject {
  constructor(source, defWeight = 0) {
    this.term = '';
    this._soundsLike = [];

    if (typeof source === 'object') {
      const {
        term, soundsLike, weight, id,
      } = source;

      this.term = term || '';
      this.weight = weight || 0;

      if (soundsLike) {
        if (Array.isArray(soundsLike)) {
          this._soundsLike = [...soundsLike];
        } else if (_.isString(soundsLike)) {
          this._soundsLike = soundsLike.split(',');
        }
      }

      if (id) {
        this.id = id;
      }
    } else if (typeof source === 'string') {
      const split = source.split(',');
      this.term = split.shift();
      this.duplicate = false;
      this.weight = split.length ? split.shift() : defWeight;
      this.soundsLike = split;
    }

    if (!this.id) {
      this.id = uuid();
    }
  }

  has(searchTerm) {
    if (!searchTerm) return true;
    if (this.empty) return false;

    const data = [this.term, ..._(this.soundsLike).reject('empty').map('soundsLike')];
    const result = search(searchTerm, { data });
    return result;
  }

  clone() {
    return new VocabularyListTermObject(this);
  }

  get empty() {
    return !this.term;
  }

  get badWeight() {
    return _.clamp(this.weight, 0, MAX_WEIGHT) !== this.weight;
  }

  get soundsLike() {
    if (!this._soundsLike) this._soundsLike = [];
    return this._soundsLike;
  }

  set soundsLike(value) {
    this._soundsLike = SoundsLikePhraseObject.listToPhrases(value);
  }

  get weight() {
    if (!this._weight) {
      this._weight = 0;
    }
    return this._weight;
  }

  set weight(value) {
    let weight = value;
    if (typeof value === 'string') {
      weight = parseInt(value, 10) || 0;
    } else if (typeof value !== 'number') {
      weight = 0;
    }

    this._weight = _.clamp(Math.floor(weight), 0, MAX_WEIGHT);
  }

  get term() {
    return this._term;
  }

  set term(value) {
    if (!_.isString(value)) {
      console.log('bad term name', value);
      this._term = '';
    }
    this._term = _.trim(value, /['" ]+/);
  }

  toJSON() {
    return {
      term: this.term,
      soundsLike: this._soundsLike,
      weight: this.weight,
    };
  }

  toCSV() {
    const soundsLike = this._soundsLike.map(s => `${s}`);
    return [`${this.term}`, this.weight, ...soundsLike].join(',');
  }

  static listToData(...input) {
    return _(input)
      .flattenDeep()
      .compact()
      .reject('empty')
      .invokeMap('toJSON')
      .uniqBy('term')
      .sortBy('term')
      .value();
  }

  static toList(...input) {
    let defaultWeight = 0;
    const cleanKeywordObjectList = _(input)
      .flattenDeep()
      .compact()
      .tap((list) => {
        if (_.isNumber(_.last(list))) {
          defaultWeight = list.pop();
        }
        return list;
      })
      .map((string) => {
        if (string instanceof VocabularyListTermObject) {
          return string;
        }
        return new VocabularyListTermObject(string);
      })
      .filter(term => term.term)
      .value();

    while ((cleanKeywordObjectList.length < TOTAL_ROWS) || cleanKeywordObjectList.slice(-ROWS_AT_END)
      .filter(a => !a.empty).length) {
      cleanKeywordObjectList.push(new VocabularyListTermObject({ weight: defaultWeight }));
    }
    return cleanKeywordObjectList;
  }

  static termsToData(terms) {
    if ((!terms) || typeof terms !== 'string') {
      return [];
    }
    const data = _(terms.split('\n'))
      .map(_.trim)
      .filter(_.identity)
      .value();
    return VocabularyListTermObject.toList(data);
  }

  static hasItems(list) {
    for (let i = 0; i < list.length; i += 1) {
      const item = list[i];
      if (item instanceof VocabularyListTermObject && (!item.empty)) return true;
    }

    return false;
  }
}

export default VocabularyListTermObject;
